import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks';
import { Loading } from '../ui';
import { env } from '../../config';

export function RequirePermission({ 
  children, 
  permissions = [], 
  requireAll = false,
  redirectTo = '/dashboard',
  showForbidden = false 
}) {
  const { data: user, isLoading, isFetching } = useAuth({ enabled: !env.features.authBypass });
  const location = useLocation();

  // If auth bypass is enabled, render children directly
  if (env.features.authBypass) {
    return <>{children}</>;
  }

  // Show loading while checking authentication
  if (isLoading || isFetching) {
    return <Loading messageKey="auth.checkPermissions" />;
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  // Admin always has access to everything
  const isAdmin = user?.role === 'admin' || user?.roles?.includes('admin');
  
  if (isAdmin) {
    return <>{children}</>;
  }

  // Check if user has required permissions
  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || user?.can?.[permission] === true;
  };

  const hasRequiredPermissions = () => {
    if (permissions.length === 0) return true;
    
    if (requireAll) {
      // User must have ALL permissions
      return permissions.every(permission => hasPermission(permission));
    } else {
      // User must have at least ONE permission
      return permissions.some(permission => hasPermission(permission));
    }
  };

  // If user doesn't have required permissions
  if (!hasRequiredPermissions()) {
    if (showForbidden) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 max-w-md text-center">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-sm text-gray-600 mb-6">
              You don't have permission to access this page. Please contact your administrator if you need access.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
    
    return <Navigate to={redirectTo} replace state={{ error: 'Insufficient permissions' }} />;
  }

  return <>{children}</>;
}

